import {
  ActionIcon,
  Avatar,
  Combobox,
  TextInput,
  useCombobox,
  Text,
  type TextInputProps,
  Stack,
  Flex,
  Group,
} from '@mantine/core'
import { useViewportSize } from '@mantine/hooks'
import { useStatsigClient } from '@statsig/react-bindings'
import { IconSearch, IconX } from '@tabler/icons-react'
import type { SearchClient } from 'algoliasearch'
import { SearchParameters } from 'algoliasearch-helper'
import type { Hit, HitHighlightResult } from 'instantsearch.js'
import { useRouter } from 'next/router'
import React, { useEffect, useMemo, useState } from 'react'
import {
  Configure,
  type HitsProps,
  Index,
  InstantSearch,
  type SearchBoxProps,
  useHits,
  useInstantSearch,
  useSearchBox,
} from 'react-instantsearch'
import Markdown from 'react-markdown' // Cannot be updated because of issue with trailing spaces in markdown - https://github.com/sudolabs-io/the-expert/issues/3519
import sanitizeHtml from 'sanitize-html'
import { ROUTE } from 'shared-constants'
import slugify from 'slugify'
import styled from 'styled-components'
import TypesenseInstantSearchAdapter from 'typesense-instantsearch-adapter'
import { ICON_SIZE } from 'components/Icon'
import Icon from 'components/Icon/Icon'
import { type ExpertHit } from 'components/InstantSearch/ExpertHits'
import { Box } from 'components/Layout'
import { addUnderlineSupport } from 'components/MarkdownRenderer'
import { environment } from 'constants/environment'
import { STATSIG_EVENTS } from 'providers/statsig'
import { useUserAuth } from 'providers/userAuth'
import { COLOR_INTENT, FONT_STACK, SPACE } from 'Theme'
import { useMedia } from 'useMedia'
import { getProductSlug } from 'utils/getProductSlug'
import {
  getTypesenseConfigurationOptions,
  getTypesenseClient,
} from 'utils/typesense'
import classNames from './Search.module.css'

interface Props {
  searchString: string
  setSearchString: React.Dispatch<React.SetStateAction<string>>
  isDropdownVisible: boolean
  setIsDropdownVisible: React.Dispatch<React.SetStateAction<boolean>>
  onOptionSelect: (hit: Hit) => void
  onNewSearch: (query: string) => void
  isSearchInputFocused: boolean
  algoliaClient?: SearchClient
  onInputFocus?: () => void
  onInputBlur?: () => void
}

type ResultsCount = {
  experts: number
  categories: number
  brands: number
  products: number
  popularSearches: number
}

type SearchLoadingStatus = {
  experts: boolean
  categories: boolean
  brands: boolean
  products: boolean
  popularSearches: boolean
}

interface SearchQueryContextProps {
  query: string | undefined
  setQuery: React.Dispatch<React.SetStateAction<string | undefined>>
  resultsCount: ResultsCount
  setResultsCount: React.Dispatch<React.SetStateAction<ResultsCount>>
  loading: SearchLoadingStatus
  setLoading: React.Dispatch<React.SetStateAction<SearchLoadingStatus>>
}

const SearchQueryContext = React.createContext<
  SearchQueryContextProps | undefined
>(undefined)

const useSearchQuery = () => {
  const context = React.useContext(SearchQueryContext)
  if (!context) {
    throw new Error('useSearchQuery must be used within a SearchQueryProvider')
  }
  return context
}

const SearchQueryProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [query, setQuery] = useState('')
  const [resultsCount, setResultsCount] = useState<ResultsCount>({
    experts: 0,
    categories: 0,
    brands: 0,
    products: 0,
    popularSearches: 0,
  })
  const [loading, setLoading] = useState<SearchLoadingStatus>({
    experts: false,
    categories: false,
    brands: false,
    products: false,
    popularSearches: false,
  })
  const value = useMemo(
    () => ({
      query,
      setQuery,
      resultsCount,
      setResultsCount,
      loading,
      setLoading,
    }),
    [query, resultsCount, loading]
  )

  return (
    <SearchQueryContext.Provider value={value}>
      {children}
    </SearchQueryContext.Provider>
  )
}

const toSlug = (text: string) => slugify(text, { lower: true, strict: true })

const StyledMarkdown = styled(Markdown)`
  font-weight: 400;

  mark {
    font-weight: 500;
    background-color: unset;
    color: unset;
  }
`

const HitRenderer = ({ markdownSource, ...props }) => (
  <Text {...props}>
    <StyledMarkdown
      source={addUnderlineSupport(sanitizeHtml(markdownSource))}
      escapeHtml={false}
      linkTarget="_blank"
    />
  </Text>
)

const ProductHits = (
  props: HitsProps<{
    id: string
    title: string
    imageUrl: string
    legacyProductId: number
  }>
) => {
  const { hits } = useHits(props)
  const { setResultsCount, setLoading } = useSearchQuery()
  const { status } = useInstantSearch()

  useEffect(() => {
    if (status === 'loading' || status === 'stalled') {
      setLoading((prev) => ({ ...prev, products: true }))
    } else {
      setLoading((prev) => ({ ...prev, products: false }))
    }
  }, [status, setLoading])

  useEffect(() => {
    setResultsCount((prev) => ({ ...prev, products: hits.length }))
  }, [hits, setResultsCount])

  return (
    <>
      {hits.length > 0 && (
        <Text size="xs" px={16} py={10}>
          Products
        </Text>
      )}
      {hits.map((hit) => (
        <Combobox.Option
          value={ROUTE.SHOWROOM_PRODUCT_DETAIL({
            slug: getProductSlug({
              id: hit.legacyProductId,
              title: hit.title,
            }),
          })}
          key={hit.id}
          py={8}
          px={20}
        >
          <Flex wrap="nowrap" gap="sm" align="center">
            <Avatar maw={30} radius="sm" src={hit.imageUrl} size={30} />
            <HitRenderer
              size="sm"
              markdownSource={
                // eslint-disable-next-line no-underscore-dangle
                (hit._highlightResult.title as HitHighlightResult).value
              }
            />
          </Flex>
        </Combobox.Option>
      ))}
    </>
  )
}

const QueryRefinement = (props: SearchBoxProps) => {
  const { query, refine, clear } = useSearchBox(props)
  const { query: searchQuery } = useSearchQuery()

  useEffect(() => {
    if (searchQuery !== query) {
      if (typeof searchQuery === 'undefined') {
        clear()
      } else {
        refine(searchQuery)
      }
    }
  }, [searchQuery, query, refine, clear])

  return null
}

const PopularSearchesHits = (
  props: HitsProps<{
    q: string
  }>
) => {
  const { hits } = useHits(props)
  const { setResultsCount, setLoading } = useSearchQuery()
  const { status } = useInstantSearch()

  useEffect(() => {
    if (status === 'loading' || status === 'stalled') {
      setLoading((prev) => ({ ...prev, popularSearches: true }))
    } else {
      setLoading((prev) => ({ ...prev, popularSearches: false }))
    }
  }, [status, setLoading])

  useEffect(() => {
    setResultsCount((prev) => ({ ...prev, popularSearches: hits.length }))
  }, [hits, setResultsCount])

  return (
    <>
      {hits.length > 0 && (
        <Text size="xs" px={16} py={10}>
          Popular searches
        </Text>
      )}
      {hits.map((hit) => (
        <Combobox.Option
          value={ROUTE.SEARCH({
            q: hit.q,
          })}
          key={hit.q}
          py={8}
          px={20}
        >
          <Flex wrap="nowrap" gap="sm" align="center">
            <HitRenderer
              size="sm"
              markdownSource={
                // eslint-disable-next-line no-underscore-dangle
                (hit._highlightResult.q as HitHighlightResult).value
              }
            />
          </Flex>
        </Combobox.Option>
      ))}
    </>
  )
}

const ExpertHits = (props: HitsProps<ExpertHit>) => {
  const { hits } = useHits(props)
  const { setResultsCount, setLoading } = useSearchQuery()
  const { status } = useInstantSearch()

  useEffect(() => {
    if (status === 'loading' || status === 'stalled') {
      setLoading((prev) => ({ ...prev, experts: true }))
    } else {
      setLoading((prev) => ({ ...prev, experts: false }))
    }
  }, [status, setLoading])

  useEffect(() => {
    setResultsCount((prev) => ({ ...prev, experts: hits.length }))
  }, [hits, setResultsCount])

  return (
    <>
      {hits.length > 0 && (
        <Text size="xs" px={16} py={10}>
          Experts
        </Text>
      )}
      {hits.map((hit) => (
        <Combobox.Option
          value={ROUTE.FIND_AN_EXPERT_CONSULTATION_TAB_DYNAMIC({
            slug: hit.id,
          })}
          key={hit.id}
          py={8}
          px={20}
        >
          <Flex wrap="nowrap" align="center" gap="sm">
            <Avatar
              maw={30}
              src={hit.headShotImageUrl}
              alt={hit.profileName}
              size={30}
            />
            <Stack flex={1} w="100%" gap={0}>
              <HitRenderer
                size="sm"
                markdownSource={
                  // eslint-disable-next-line no-underscore-dangle
                  (hit._highlightResult.profileName as HitHighlightResult).value
                }
              />
              <Text size="xs">{hit.jobTitle}</Text>
            </Stack>
          </Flex>
        </Combobox.Option>
      ))}
    </>
  )
}

const typesenseClient = getTypesenseClient()

const BrandHits = () => {
  const [items, setItems] = useState([])
  const { query, setResultsCount, setLoading } = useSearchQuery()
  const { status } = useInstantSearch()

  useEffect(() => {
    if (status === 'loading' || status === 'stalled') {
      setLoading((prev) => ({ ...prev, brands: true }))
    } else {
      setLoading((prev) => ({ ...prev, brands: false }))
    }
  }, [status, setLoading])

  useEffect(() => {
    setResultsCount((prev) => ({ ...prev, brands: items.length }))
  }, [items, setResultsCount])

  useEffect(() => {
    const fetchItems = () => {
      typesenseClient
        .collections('products-v3')
        .documents()
        .search({
          q: query || '*',
          query_by: 'brand',
          group_by: 'brand',
          filter_by: `status:=PUBLISHED && isWhiteLabel:=false`,
        })
        .then(({ grouped_hits }) => {
          const results = grouped_hits.reduce((acc, { group_key, hits }) => {
            const firstHighlight = hits[0].highlight
            const firstHitKey = Object.keys(firstHighlight)[0] // Which key had the hit

            acc.set(firstHighlight[firstHitKey].snippet, group_key)

            return acc
          }, new Map<string, string[]>())

          setItems(
            [...results].map(([key, value]) => ({
              label: key,
              value: ROUTE.SHOWROOM_BRAND_DETAIL({
                slug: toSlug(value[0]),
              }),
            }))
          )
        })
    }
    fetchItems()
  }, [query])

  return (
    <>
      {items.length > 0 && (
        <Text size="xs" px={16} py={10}>
          Brands
        </Text>
      )}
      {items.map((item) => (
        <Combobox.Option value={item.value} key={item.value} py={8} px={20}>
          <HitRenderer size="sm" markdownSource={item.label} />
        </Combobox.Option>
      ))}
    </>
  )
}

const CategoriesHits = () => {
  const [items, setItems] = useState([])
  const { query, setResultsCount, setLoading } = useSearchQuery()
  const { status } = useInstantSearch()

  useEffect(() => {
    if (status === 'loading' || status === 'stalled') {
      setLoading((prev) => ({ ...prev, products: true }))
    } else {
      setLoading((prev) => ({ ...prev, products: false }))
    }
  }, [status, setLoading])

  useEffect(() => {
    setResultsCount((prev) => ({ ...prev, categories: items.length }))
  }, [items, setResultsCount])

  useEffect(() => {
    const fetchItems = () => {
      typesenseClient
        .collections('products-v3')
        .documents()
        .search({
          q: query || '*',
          // We want to get as specific category first as possible - for example if the
          // user searches for Furniture we want to show subcategories like Outdoor Furniture along
          // with root category Furniture
          query_by: 'categories',
          // This needs to be in reverse order since category2 can be empty and otherwise Typesense
          // will complain about unbalanced && operands (https://github.com/typesense/typesense/issues/2559)
          group_by: 'categories',
          filter_by: `status:=PUBLISHED && isWhiteLabel:=false`,
        })
        .then(({ grouped_hits }) => {
          const results = grouped_hits.reduce((acc, { group_key, hits }) => {
            const lastHighlight = hits.at(-1).highlights

            acc.set(lastHighlight.at(-1).snippets.join(' > '), group_key.flat())

            return acc
          }, new Map<string, string[]>())

          const sortedKeys = Array.from(results.keys()).sort()

          setItems(
            sortedKeys.map((key) => ({
              label: key,
              value: `${ROUTE.SHOWROOM_CATEGORY()}/${results.get(key)?.map(toSlug).join('/')}`,
            }))
          )
        })
    }
    fetchItems()
  }, [query])

  return (
    <>
      {items.length > 0 && (
        <Text size="xs" px={16} py={10}>
          Categories
        </Text>
      )}
      {items.slice(0, 5).map((item) => (
        <Combobox.Option value={item.value} key={item.value} py={8} px={20}>
          <HitRenderer size="sm" markdownSource={item.label} />
        </Combobox.Option>
      ))}
    </>
  )
}

const SearchTextInputTarget = (props: TextInputProps) => {
  const { query, setQuery } = useSearchQuery()
  const router = useRouter()
  const { client: statsigClient } = useStatsigClient()

  const navigateToSearch = () => {
    router.push(`/search?q=${query}`)
  }
  const inputRef = React.useRef<HTMLInputElement>(null)

  return (
    <Combobox.Target>
      <TextInput
        {...props}
        ref={inputRef}
        value={query}
        onChange={(event) => {
          setQuery(event.currentTarget.value)
          if (statsigClient) {
            statsigClient.logEvent(
              STATSIG_EVENTS.SEARCH_INPUT,
              event.target.value
            )
          }
        }}
        w="100%"
        rightSectionProps={{
          style: {
            width: 64,
          },
        }}
        rightSection={
          <Group gap={0}>
            <ActionIcon
              style={{
                borderRight: '1px solid var(--mantine-color-gray-3)',
                borderRadius: '0px',
              }}
              variant="transparent"
              onClick={() => setQuery('')}
            >
              <IconX size={16} />
            </ActionIcon>
            <ActionIcon
              variant="transparent"
              onClick={() => navigateToSearch()}
            >
              <IconSearch size={16} />
            </ActionIcon>
          </Group>
        }
        placeholder="Search products, brands, experts..."
        classNames={{ input: 'custom-input' }}
        styles={(theme) => ({
          wrapper: {
            [`@media (max-width: ${theme.breakpoints.lg}px)`]: {
              width: '100%',
            },
            borderRadius: '6px',
            borderColor: '#E1E1E1',
          },
          input: {
            color: 'black',
            '::placeholder': {
              color: '#757575 !important', // 👈 placeholder color
              opacity: 1, // ensures consistent color across browsers
            },
            fontFamily: FONT_STACK.SANS,
            borderRadius: '6px ',
          },
        })}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            props.onKeyDown(e)
            inputRef.current?.blur()
          }
          if (e.key === 'Enter') {
            inputRef.current?.blur()
            e.preventDefault()
            navigateToSearch()
          }
        }}
      />
    </Combobox.Target>
  )
}

const { searchClient: productExpertsSearchClient } =
  new TypesenseInstantSearchAdapter({
    server: getTypesenseConfigurationOptions(),
    collectionSpecificSearchParameters: {
      'products-v3': {
        query_by: 'title',
        sort_by: 'updatedAt:desc',
        include_fields: 'title,imageUrl,legacyProductId',
        filter_by: `status:=PUBLISHED && isWhiteLabel:=false`,
      },
      'experts-v3': {
        query_by: 'profileName',
        filter_by: `isPublicVisible:=true`,
        include_fields: 'profileName,headShotImageUrl,id,jobTitle',
        sort_by: 'order:asc,profileName:asc',
      },
    },
  })

const { searchClient: brandsSearchClient } = new TypesenseInstantSearchAdapter({
  server: getTypesenseConfigurationOptions(),
  additionalSearchParameters: {
    query_by: 'brand',
    filter_by: `status:=PUBLISHED && isWhiteLabel:=false`,
    include_fields: 'brand',
    facet_by: 'brand',
    maxValuesPerFacet: 5,
  },
})

const { searchClient: categorySearchClient } =
  new TypesenseInstantSearchAdapter({
    server: getTypesenseConfigurationOptions(),
    additionalSearchParameters: {
      query_by: 'categories',
      facet_by: 'categories',
      maxValuesPerFacet: 5,
    },
  })

const { searchClient: popularSearchesSearchClient } =
  new TypesenseInstantSearchAdapter({
    server: getTypesenseConfigurationOptions(),
    additionalSearchParameters: {
      query_by: 'q',
    },
  })

const SearchResults = () => {
  const { query, resultsCount, loading } = useSearchQuery()
  const isQueryOverTwoChars = query.length > 2
  const { height } = useViewportSize()
  const { client: statsigClient } = useStatsigClient()
  const { user } = useUserAuth()

  const searchParameters = useMemo(
    () =>
      new SearchParameters({
        hitsPerPage: 5,
      }),
    []
  )

  const handleClickEnquire = () => {
    if (statsigClient) {
      statsigClient.logEvent(STATSIG_EVENTS.NO_RESULT_SEARCH_NOTE, user?.email)
    }
    if (environment.CUSTOMER_SERVICE_FRAMEWORK === 'intercom') {
      window?.Intercom?.('show')
    } else {
      window?.FrontChat?.('show')
    }
  }

  const hasResults = Object.values(resultsCount).some((value) => value > 0)

  const isLoading = Object.values(loading).some((value) => value)

  return isQueryOverTwoChars ? (
    <Combobox.Dropdown
      w={600}
      onMouseDown={(e) => {
        e.stopPropagation()
        e.preventDefault()
      }}
    >
      <Combobox.Options mah={height - 64}>
        <InstantSearch
          searchClient={popularSearchesSearchClient}
          indexName="product-queries"
        >
          <QueryRefinement />
          <PopularSearchesHits />
        </InstantSearch>
        <InstantSearch
          searchClient={categorySearchClient}
          indexName="products-v3"
        >
          <QueryRefinement />
          <CategoriesHits />
        </InstantSearch>
        <InstantSearch
          searchClient={brandsSearchClient}
          indexName="products-v3"
        >
          <QueryRefinement />
          <BrandHits />
        </InstantSearch>
        <InstantSearch searchClient={productExpertsSearchClient}>
          <Index indexName="products-v3">
            <Configure {...searchParameters} />
            <ProductHits />
          </Index>
          <QueryRefinement />
          <Index indexName="experts-v3">
            <Configure {...searchParameters} />
            <ExpertHits />
          </Index>
        </InstantSearch>
      </Combobox.Options>
      {!hasResults && !isLoading && (
        <Box px={SPACE.PX_32} py={SPACE.PX_20}>
          <Flex mb={SPACE.PX_15} justify="center">
            <Icon.MenuSearch size={ICON_SIZE.PX_20} />
          </Flex>
          <Text
            c={COLOR_INTENT.SEARCH.NO_RESULT_TEXT}
            size="sm"
            ta="center"
            mb={SPACE.PX_10}
          >
            {`We can't find “${query}”`}
          </Text>
          <Text
            ta="center"
            c={COLOR_INTENT.LINK.PRIMARY}
            size="xs"
            style={{ cursor: 'pointer' }}
            onClick={handleClickEnquire}
            aria-label="Send note with the product you're looking for"
          >
            Send us a note with the product you&apos;re looking for - we may be
            able to source it for you
          </Text>
        </Box>
      )}
    </Combobox.Dropdown>
  ) : null
}

export const Search = React.forwardRef<HTMLDivElement, Props>(
  ({ onInputFocus, onInputBlur }) => {
    const combobox = useCombobox({
      onDropdownClose: () => combobox.resetSelectedOption(),
    })
    const router = useRouter()
    const { MOBILE: isMobile } = useMedia()

    const handleInputFocus = () => {
      onInputFocus?.()
      combobox.openDropdown()
      document.querySelector('body').style.overflow = 'hidden'
    }

    const handleInputBlur = () => {
      onInputBlur?.()
      combobox.closeDropdown()
      document.querySelector('body').style.overflow = 'auto'
    }

    return (
      <SearchQueryProvider>
        <Combobox
          onOptionSubmit={(optionValue) => {
            const active = document.activeElement as HTMLElement
            if (active) {
              active.blur()
            }
            combobox.closeDropdown()

            router.push(optionValue)
            document.querySelector('body').style.overflow = 'auto'
          }}
          store={combobox}
          classNames={
            isMobile
              ? { dropdown: classNames.dropdown, options: classNames.options }
              : {}
          }
        >
          <SearchTextInputTarget
            onClick={() => combobox.openDropdown()}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={handleInputBlur}
          />
          <SearchResults />
        </Combobox>
      </SearchQueryProvider>
    )
  }
)
